import { ArrowRightAlt } from "@mui/icons-material";
import { Skeleton, Stack, Typography } from "@mui/material";
import copy from "fast-copy";
import moment from 'moment';
import React from "react";
import { CustomTimeline, CustomTimelineItem, UserAvatar, UserStack } from "../../components";
import { useLazyGetUserAssignmentByIdQuery, UserAssignment } from "../../redux/services";
import { arraysAreDifferent } from "../../utils";

export const AssignHistory: React.FC<{ user_assignment_id: string }> = ({ user_assignment_id }) => {

    const [getUserAssignment, userAssignmentData] = useLazyGetUserAssignmentByIdQuery();

    React.useEffect(() => {
        getUserAssignment({ id: user_assignment_id, include_histories: true });
        // eslint-disable-next-line
    }, []);

    if (userAssignmentData.isFetching || userAssignmentData.isLoading) {
        return <Stack gap={1}>
            <Skeleton variant="rounded" width={"100%"} />
            <Skeleton variant="rounded" height={"100px"} width={"100%"} />
            <Skeleton variant="rounded" height={"200px"} width={"100%"} />
        </Stack>
    }

    const histories: UserAssignment[] = [
        ...(userAssignmentData.data ? [userAssignmentData.data] : []),
        ...(copy(userAssignmentData.data?.history)?.reverse() ?? [])
    ]

    const timeLineItems: CustomTimelineItem[] = [];

    histories?.forEach((history, index) => {

        const oldHistory = histories[index + 1];

        let assignedUser: { id: string, name: string } | null = null;
        let workflowTag: string | null = null;
        let notes: string | null = null;
        let followers: string[] | undefined = [];

        if (history.assigned_user_id !== oldHistory?.assigned_user_id) {
            assignedUser = {
                id: history.assigned_user_id,
                name: `${history.assigned_user_name}`
            }
        };


        if (history.workflow_tag?.tag_id !== oldHistory?.workflow_tag?.tag_id) {
            workflowTag = `${history.workflow_tag?.tag_name}`
        };

        if (history.notes !== oldHistory?.notes) {
            notes = history.notes
        };

        if (arraysAreDifferent(history.followers, oldHistory?.followers)) {
            followers = history.followers
        };

        if (assignedUser || workflowTag || notes || followers?.length) {
            timeLineItems.push({
                type: 'history',
                content: <Stack>
                    {/* Header */}
                    <Stack
                        gap={1}
                        direction={"row"}
                        alignItems={"center"}
                        sx={{
                            backgroundColor: (theme) => theme.palette.background.default,
                            p: 1,
                            borderTopRightRadius: '8px',
                            borderTopLeftRadius: '8px',
                        }}
                    >
                        <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"}>
                            <UserAvatar userId={`${history?.created_user_id}`} name={`${history?.created_user ?? ""}`} sx={{ height: 20, width: 20, fontSize: "8px" }} />
                            <Typography variant="subtitle2">{history.created_user}</Typography>
                            <Typography variant="caption" color="textSecondary" fontFamily={"htrts_medium"}>
                                {moment(history.created_time).format('lll')}
                            </Typography>
                        </Stack>
                    </Stack>

                    <Stack gap={1} sx={{
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        minHeight: '16px',
                        p: 1
                    }}>

                        {assignedUser && <Typography variant="caption" color="textSecondary" sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                            Assigned User:

                            {oldHistory && <>
                                <UserAvatar userId={oldHistory.assigned_user_id} name={`${oldHistory.assigned_user_name}`} sx={{ height: 16, width: 16, fontSize: '8px' }} />
                                <span style={{ fontFamily: "htrts_medium", textDecoration: "line-through" }}>{oldHistory.assigned_user_name}</span>
                                <ArrowRightAlt fontSize="small" color="secondary" />
                            </>}


                            <UserAvatar userId={assignedUser.id} name={assignedUser.name} sx={{ height: 16, width: 16, fontSize: '8px' }} />
                            <span style={{ fontFamily: "htrts_medium" }}>{assignedUser.name}</span>
                        </Typography>}

                        {workflowTag && <Typography variant="caption" color="textSecondary" sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                            Workflow:

                            {oldHistory && <>
                                <span style={{ fontFamily: "htrts_medium", textDecoration: "line-through" }}>{oldHistory.workflow_tag?.tag_name}</span>
                                <ArrowRightAlt fontSize="small" color="secondary" />
                            </>}


                            <span style={{ fontFamily: "htrts_medium" }}>{workflowTag}</span>
                        </Typography>}

                        {notes && <Typography variant="caption" color="textSecondary" sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                            Notes:

                            {oldHistory && <>
                                <span style={{ fontFamily: "htrts_medium", textDecoration: "line-through" }}>{oldHistory.notes}</span>
                                <ArrowRightAlt fontSize="small" color="secondary" />
                            </>}


                            <span style={{ fontFamily: "htrts_medium" }}>{notes}</span>
                        </Typography>}

                        {followers && followers.length > 0 && <Typography variant="caption" color="textSecondary" >Followers:

                            <Stack direction={"row"} gap={0.5} alignItems={"center"}>
                                {oldHistory && <>
                                    <UserStack
                                        avatarSize="small"
                                        hideHeader={true}
                                        hideBorder={true}
                                        user={oldHistory.followers}
                                        max={50}
                                    />
                                    <ArrowRightAlt fontSize="small" color="secondary" />
                                </>}

                                <UserStack
                                    avatarSize="small"
                                    hideHeader={true}
                                    hideBorder={true}
                                    user={followers}
                                    max={50}
                                />
                            </Stack>
                        </Typography>}
                    </Stack>
                </Stack>
            })
        }
    });

    return <CustomTimeline items={timeLineItems} />
}